// =============================================
// bulkNsChange — zbiorcza zmiana nameserverow w AfterMarket (panel "Zmiana DNS",
// tryb Bulk). Dowolne NS (nie tylko z zony CF): jeden WSPOLNY zestaw dla
// zaznaczonych domen + opcjonalny OSOBNY zestaw per domena.
//
// Czyste funkcje (parse/validate/plan) + runner z wstrzykiwanymi deps (testy
// bez sieci). Zasady jak w transferRunner:
//  * SEKWENCYJNIE, nigdy rownolegle — zly NS = domena offline
//  * domena z nieznanymi obecnymi NS (nie pobrano / blad ns/get) NIE jest
//    zmieniana — najpierw trzeba zobaczyc, co sie nadpisuje
//  * cel == obecne -> skip (zero wywolan API)
//  * 3 bledy z rzedu -> auto-stop, reszta zostaje "pending"
//  * log best-effort do dns_change_log (akcja ns_set, before/after)
//  * JEDNO konto AfterMarket na run (workspace) — guard Sponsorakow w UI + tu
//    workspace jest parametrem, nie polem wiersza (wiersze nie moga go podmienic)
// =============================================
import type { AmWorkspace } from "@/services/aftermarketService";
import type { DnsChangeLogInsert } from "@/services/cloudflare/dnsChangeLogService";

const BULK_NS_MAX_CONSECUTIVE_FAILURES = 3;
const HOST_RE = /^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** "a.ns.x.com, b.ns.x.com\nc..." -> znormalizowana lista (lowercase, bez kropki na koncu, bez duplikatow). */
export function parseNsInput(text: string | string[] | null | undefined): string[] {
  const raw = Array.isArray(text) ? text.join("\n") : text || "";
  const out: string[] = [];
  for (const part of raw.split(/[\s,;]+/)) {
    const ns = part.trim().toLowerCase().replace(/\.$/, "");
    if (ns && !out.includes(ns)) out.push(ns);
  }
  return out;
}

/** null = OK; inaczej komunikat dla uzytkownika. */
export function validateNs(ns: string[]): string | null {
  if (ns.length < 2) return "Podaj co najmniej 2 nameservery";
  if (ns.length > 8) return "Za dużo nameserverów (max 8)";
  const bad = ns.find((n) => !HOST_RE.test(n));
  if (bad) return `„${bad}” nie wygląda na nazwę serwera NS`;
  return null;
}

export function sameNsSet(a: string[], b: string[]): boolean {
  const na = parseNsInput(a).sort();
  const nb = parseNsInput(b).sort();
  return na.length === nb.length && na.every((x, i) => x === nb[i]);
}

export interface BulkNsRowInput {
  /** znormalizowana domena (klucz) */
  domain: string;
  websiteId: string | null;
  /** null = nie pobrano / blad pobierania; [] = AfterMarket zwrocil brak NS */
  currentNs: string[] | null;
  /** blad ns/get (np. domeny nie ma na tym koncie) */
  fetchError?: string | null;
  /** true = domena ma OSOBNE NS (customNs), false = bierze wspolne */
  custom: boolean;
  customNs: string[];
}

type BulkNsSkip = "same" | "invalid_target" | "no_current";

interface BulkNsPlanRow {
  domain: string;
  websiteId: string | null;
  currentNs: string[] | null;
  targetNs: string[];
  /** skad cel: wspolne czy osobne dla tej domeny */
  source: "shared" | "custom";
  action: "change" | "skip";
  skip?: BulkNsSkip;
  reason?: string;
}

interface BulkNsPlan {
  rows: BulkNsPlanRow[];
  changeCount: number;
  skipCount: number;
}

export function buildBulkNsPlan(inputs: BulkNsRowInput[], sharedNs: string[]): BulkNsPlan {
  const shared = parseNsInput(sharedNs);
  const rows: BulkNsPlanRow[] = inputs.map((r) => {
    const source = r.custom ? "custom" : "shared";
    const targetNs = r.custom ? parseNsInput(r.customNs) : shared;
    const base = { domain: r.domain, websiteId: r.websiteId, currentNs: r.currentNs, targetNs, source } as const;
    if (r.currentNs === null) {
      return { ...base, action: "skip", skip: "no_current", reason: r.fetchError ? `nie pobrano obecnych NS: ${r.fetchError}` : "nie pobrano obecnych NS" };
    }
    const invalid = validateNs(targetNs);
    if (invalid) return { ...base, action: "skip", skip: "invalid_target", reason: `${source === "custom" ? "osobne NS" : "wspólne NS"}: ${invalid}` };
    if (sameNsSet(r.currentNs, targetNs)) return { ...base, action: "skip", skip: "same", reason: "już ustawione" };
    return { ...base, action: "change" };
  });
  const changeCount = rows.filter((r) => r.action === "change").length;
  return { rows, changeCount, skipCount: rows.length - changeCount };
}

// ---------------------------------------------------------------------------
// Runner
// ---------------------------------------------------------------------------

type BulkNsRowStatus = "pending" | "running" | "ok" | "failed" | "skipped";

export interface BulkNsRowState {
  domain: string;
  status: BulkNsRowStatus;
  message?: string;
}

export interface BulkNsSummary {
  total: number;
  ok: number;
  failed: number;
  skipped: number;
  stoppedReason: null | "consecutive_failures" | "user";
}

interface BulkNsDeps {
  nsSet: (name: string, ns: string[], workspace: AmWorkspace) => Promise<boolean>;
  log: (row: DnsChangeLogInsert) => Promise<void>;
}

export async function runBulkNs(
  plan: BulkNsPlan,
  workspace: AmWorkspace,
  deps: BulkNsDeps,
  onRow: (state: BulkNsRowState) => void,
  opts: { isStopRequested?: () => boolean; maxConsecutiveFailures?: number } = {},
): Promise<BulkNsSummary> {
  const max = opts.maxConsecutiveFailures ?? BULK_NS_MAX_CONSECUTIVE_FAILURES;
  const summary: BulkNsSummary = { total: plan.rows.length, ok: 0, failed: 0, skipped: 0, stoppedReason: null };
  let consecutive = 0;

  for (const row of plan.rows) {
    if (opts.isStopRequested?.()) { summary.stoppedReason = "user"; break; }
    if (consecutive >= max) { summary.stoppedReason = "consecutive_failures"; break; }

    const logBase = { website_id: row.websiteId, domain: row.domain, action: "ns_set" as const, cf_account_id: null, workspace, before: row.currentNs, after: row.targetNs };

    if (row.action === "skip") {
      summary.skipped++;
      onRow({ domain: row.domain, status: "skipped", message: row.reason });
      // "już ustawione" nie jest zdarzeniem wartym wiersza w Historii; pozostale powody tak
      if (row.skip !== "same") await deps.log({ ...logBase, status: "skipped", error: row.reason ?? null });
      continue;
    }

    onRow({ domain: row.domain, status: "running" });
    try {
      const ok = await deps.nsSet(row.domain, row.targetNs, workspace);
      if (!ok) throw new Error("AfterMarket nie potwierdził zmiany");
      summary.ok++;
      consecutive = 0;
      onRow({ domain: row.domain, status: "ok" });
      await deps.log({ ...logBase, status: "success", error: null });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      summary.failed++;
      consecutive++;
      onRow({ domain: row.domain, status: "failed", message: msg });
      await deps.log({ ...logBase, status: "failed", error: msg });
    }
  }
  return summary;
}
